import { PageHeader, Card, Badge, MarkdownView, StatBlock, EmptyState } from '../ui';
import { useBinder } from '../../lib/useBinder';
import { useFocusHighlight, focusId } from '../../lib/useFocusHighlight';
import { ShowButton } from '../common/ShowButton';
import shared from './sections.module.css';
import styles from './BestiarySection.module.css';

function optionalImage(entity: Record<string, unknown>): string | undefined {
  return typeof entity.image === 'string' ? entity.image : undefined;
}

function formatList(value: string | string[] | undefined): string | undefined {
  if (Array.isArray(value)) return value.join(', ');
  return value;
}

function actionMarkdown(action: unknown): string {
  if (typeof action === 'string') return action;
  if (action && typeof action === 'object') {
    const record = action as Record<string, unknown>;
    const name = typeof record.name === 'string' ? record.name : undefined;
    const description = typeof record.description === 'string' ? record.description : undefined;
    if (name && description) return `**${name}.** ${description}`;
    if (name) return `**${name}.**`;
    if (description) return description;
  }
  return String(action);
}

/** Monster stat cards for quick combat reference. */
export function BestiarySection() {
  const ctx = useBinder();
  useFocusHighlight();
  if (!ctx) return null;
  const { adventure, instance, state, slug } = ctx;
  void state;

  return (
    <div>
      <PageHeader eyebrow="Bestiario" title="Mostri e avversari" />

      {!adventure.monsters.length ? (
        <EmptyState
          icon="🐉"
          title="Nessun mostro"
          description="Questa avventura non contiene mostri preparati."
        />
      ) : (
        <div className={shared.gridWide}>
          {adventure.monsters.map((monster) => (
            <Card key={monster.id ?? monster.name} id={focusId(monster.id)} className={styles.card}>
              <div className={shared.entityHead}>
                <span className={shared.entityIcon} aria-hidden>
                  {monster.icon ?? '🐉'}
                </span>
                <h2 className={shared.entityName}>{monster.name}</h2>
                <div className={shared.cardActions}>
                  {monster.cr != null && <Badge tone="gold">GS {monster.cr}</Badge>}
                  <ShowButton
                    slug={slug}
                    image={optionalImage(monster)}
                    title={monster.name}
                    instanceId={instance.id}
                  />
                </div>
              </div>

              <div className={shared.tagRow}>
                {monster.ac != null && <span className={shared.chip}>CA {monster.ac}</span>}
                {monster.hp != null && <span className={shared.chip}>PF {monster.hp}</span>}
                {monster.speed && <span className={shared.chip}>Vel. {monster.speed}</span>}
                {monster.size && <span className={shared.chip}>{monster.size}</span>}
                {monster.type && <span className={shared.chip}>{monster.type}</span>}
              </div>

              {monster.stats && (
                <StatBlock
                  abilities={{
                    strength: monster.stats.for,
                    dexterity: monster.stats.des,
                    constitution: monster.stats.cos,
                    intelligence: monster.stats.int,
                    wisdom: monster.stats.sag,
                    charisma: monster.stats.car,
                  }}
                />
              )}

              <div className={styles.statLines}>
                {monster.senses && (
                  <div>
                    <span className={styles.lineLabel}>Sensi:</span> {monster.senses}
                  </div>
                )}
                {monster.languages && (
                  <div>
                    <span className={styles.lineLabel}>Linguaggi:</span> {monster.languages}
                  </div>
                )}
                {formatList(monster.damageResistances) && (
                  <div>
                    <span className={styles.lineLabel}>Resistenze:</span>{' '}
                    {formatList(monster.damageResistances)}
                  </div>
                )}
                {formatList(monster.damageImmunities) && (
                  <div>
                    <span className={styles.lineLabel}>Immunità ai danni:</span>{' '}
                    {formatList(monster.damageImmunities)}
                  </div>
                )}
                {formatList(monster.conditionImmunities) && (
                  <div>
                    <span className={styles.lineLabel}>Immunità alle condizioni:</span>{' '}
                    {formatList(monster.conditionImmunities)}
                  </div>
                )}
              </div>

              {!!monster.actions?.length && (
                <div className={shared.sectionBlock}>
                  <div className={shared.blockLabel}>Azioni</div>
                  <ul className={styles.actions}>
                    {monster.actions.map((action, index) => (
                      <li key={index}>
                        <MarkdownView inline>{actionMarkdown(action)}</MarkdownView>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {monster.notes && (
                <div className={shared.sectionBlock}>
                  <div className={shared.blockLabel}>Note</div>
                  <MarkdownView>{monster.notes}</MarkdownView>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
